import React, { useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import useApiContext from 'hooks/useApiContext';
import useRepo from 'hooks/useRepo';
import { toast } from 'react-toastify';
import {
  Box,
  Typography,
  TextField,
  InputAdornment,
  CircularProgress,
} from '@mui/material';
import Button from '@mui/material/Button';
import SearchIcon from '@mui/icons-material/Search';
import { useTranslation } from 'react-i18next';
import s from './ConnectorAdmin.module.scss';

interface iAvailableAdmin {
  id: number;
  firstname: string;
  lastname: string;
  email: string;
  company?: { id: number; name: string; title: string } | null;
}

function ConnectorAdminAdd(): JSX.Element {
  const { eventId } = useParams();
  const { eventRepository } = useApiContext();
  const { t } = useTranslation();
  const [reload, setReload] = useState(0);
  const [invitingId, setInvitingId] = useState<number | null>(null);
  const [search, setSearch] = useState('');

  const { response, loading, error, setResponse } = useRepo<iAvailableAdmin[]>(
    async () => await eventRepository.getAvailableConnectorAdmins(eventId),
    [reload]
  );

  const allAdmins = response ?? [];

  const admins = useMemo(() => {
    if (!search) return allAdmins;
    // Plain substring match (not a RegExp) so emails with '+', '.', etc. -
    // regex special characters - are matched literally instead of being
    // interpreted as a pattern.
    const needle = search.toLowerCase();
    return allAdmins.filter(
      (admin) =>
        (admin.firstname ?? '').toLowerCase().includes(needle) ||
        (admin.lastname ?? '').toLowerCase().includes(needle) ||
        (admin.email ?? '').toLowerCase().includes(needle) ||
        (admin.company?.name ?? '').toLowerCase().includes(needle)
    );
  }, [allAdmins, search]);

  const inviteAdmin = async (adminId: number): Promise<void> => {
    setInvitingId(adminId);
    try {
      await eventRepository.addConnectorAdmins(eventId, [adminId]);
      toast(t('event:admin_added_to_event'), { type: 'success' });
      // Invited admins are no longer eligible, so drop them from view right
      // away instead of waiting for a manual refresh.
      setResponse((prev) => (prev ?? []).filter((a) => a.id !== adminId));
    } catch (err: any) {
      toast(err.message, { type: 'error' });
      // The invite failed (e.g. someone else just added them) - resync with
      // the server so the list reflects reality.
      setReload((r) => r + 1);
    } finally {
      setInvitingId(null);
    }
  };

  return (
    <Box sx={{ px: { xs: 2, md: 4 }, py: 3 }}>
      {/* Page header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          mb: 2,
          gap: 2,
          flexWrap: 'wrap',
        }}
      >
        <Box>
          <Typography sx={{ fontSize: '22px', fontWeight: 700, color: '#111' }}>
            {t('event:add_admin_to_the_event')}
          </Typography>
          {!loading && (
            <Typography sx={{ fontSize: '13px', color: '#888', mt: 0.5 }}>
              {admins.length}
            </Typography>
          )}
        </Box>

        <Box
          sx={{
            display: 'flex',
            gap: 1.5,
            alignItems: 'center',
            flexWrap: 'wrap',
          }}
        >
          <TextField
            size="small"
            placeholder={t('event:search')}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 18, color: '#999' }} />
                </InputAdornment>
              ),
              sx: { borderRadius: '8px', fontSize: 13, bgcolor: '#fff' },
            }}
            sx={{ width: 220 }}
          />
        </Box>
      </Box>

      {/* Error */}
      {error && (
        <Box
          sx={{
            p: 1.5,
            bgcolor: '#fee2e2',
            borderRadius: 1,
            mb: 2,
            color: '#dc2626',
            fontSize: 13,
          }}
        >
          {error}
        </Box>
      )}

      {/* Loading */}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress size={28} sx={{ color: '#000' }} />
        </Box>
      )}

      {/* Table */}
      {!loading && (
        <Box
          sx={{
            bgcolor: '#fff',
            borderRadius: '10px',
            border: '1px solid #eee',
            overflow: 'hidden',
          }}
        >
          <Box sx={{ overflowX: 'auto', width: '100%' }}>
            <table className={s.Table}>
              <thead>
                <tr>
                  <th>{t('users:firstname')}</th>
                  <th>{t('users:lastname')}</th>
                  <th>{t('users:email')}</th>
                  <th>{t('event:company')}</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {admins.map((admin) => (
                  <tr key={admin.id}>
                    <td>{admin.firstname}</td>
                    <td>{admin.lastname}</td>
                    <td>{admin.email}</td>
                    <td>{admin.company?.name ?? '—'}</td>
                    <td>
                      <Button
                        variant="outlined"
                        size="small"
                        disabled={invitingId === admin.id}
                        onClick={async () => await inviteAdmin(admin.id)}
                        sx={{
                          fontSize: 12,
                          borderRadius: '6px',
                          textTransform: 'none',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {t('event:add')}
                      </Button>
                    </td>
                  </tr>
                ))}
                {admins.length === 0 && (
                  <tr>
                    <td
                      colSpan={5}
                      style={{ textAlign: 'center', padding: 20 }}
                    >
                      {t('event:no_admin_found')}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </Box>
        </Box>
      )}
    </Box>
  );
}

export default ConnectorAdminAdd;
